<script lang="ts">
  import Pageination from "./components/Pageination.svelte";
  import type { DataSearchResponse } from "./models/data.interface";
  import { fileStore } from "./services/select-files";
  import { showCartAlert } from "./services/cart-service";
  import MapComponent from "./components/MapComponent.svelte";
  import GenericCollectionDetails from "./components/dataset/dataset-generic/GenericCollectionDetails.svelte";
  import LocationSearchInput from "./components/search/LocationSearchInput.svelte";
  import WhenSearchInput from "./components/search/WhenSearchInput.svelte";
  import SideSearch from "./components/SideSearch.svelte";
  import { SearchState } from "./services/search-service";
  import MonthSearchInput from "./components/search/MonthSearchInput.svelte";
  import VCPDataSearchResults from "./components/search/VCPDataSearchResults.svelte";
  import CartAlert from "./components/search/CartAlert.svelte";
  import CartNotification from "./components/search/CartNotification.svelte";
  import OrderOptionsModal from "./components/order/OrderOptionsModal.svelte";
  import CheckboxSearchInput from "./components/search/CheckboxSearchInput.svelte";
  import TooltipHybridButton from "./components/search/TooltipHybridButton.svelte";
  import type { DatasetAggregations } from "./models/dataset.interface";

  const PAGE_SIZE = 20;
  export let searchEndpoint = new URL(process.env.SEARCH_URL);

  const datasetMetadata = JSON.parse(document.getElementById("datasetMetadata").innerHTML);
  const dataTypes = JSON.parse(document.getElementById("dataTypes").innerHTML).dataTypes;

  let autocompleteUrl = new URL(
    datasetMetadata.ncei.access.search?.station.params.find((p) => p.id == "stations").autocomplete
  );
  let dateFormat = datasetMetadata?.ncei?.access?.search?.when?.params?.dateformat;

  let summaryLoaded: Promise<DatasetAggregations> = fetchSummary(datasetMetadata.id);
  let datasetLoaded: Promise<DataSearchResponse>;

  let datasetSummary: DatasetAggregations;
  let searchResponse: DataSearchResponse;

  const whenComponentMap = [
    { format: "", component: null },
    { format: "None", component: null },
    { format: "Date", component: WhenSearchInput },
    { format: "DateTime", component: WhenSearchInput },
    { format: "Monthly", component: MonthSearchInput },
    { format: "Month/Day", component: MonthSearchInput },
    { format: "Month/Day/Hour", component: MonthSearchInput }
  ];

  SearchState.subscribe((search) => {
    search.params.set("limit", PAGE_SIZE.toString());
    search.params.set("offset", ((search.pageNum - 1) * PAGE_SIZE).toString());

    datasetLoaded = fetchResults(search.params);
  });

  async function fetchSummary(datasetId: string): Promise<DatasetAggregations> {
    try {
      const response = await fetch(`${searchEndpoint}/v2/datasets/${datasetId}/summary`)
      if (!response.ok) throw new Error("Failed to fetch dataset summary");

      const data = await response.json();
      if (data.aggs) {
        datasetSummary = data.aggs;
      } else {
        throw new Error("Received malformed response when requesting dataset summary")
      }

      return datasetSummary;

    } catch (err) {
      console.error("Error fetching dataset:", err);
      throw err;
    }
  }

  // When files are selected, show the CartAlert
  // $: showCartAlert.set(!!($fileStore.selectedFiles.length > 0));

  async function fetchResults(params: URLSearchParams): Promise<DataSearchResponse> {
    try {
      const response = await fetch(
        `${searchEndpoint}/v2/datasets/${datasetMetadata.id}?${params}`
      );
      if (!response.ok) throw new Error("Failed to fetch results");

      const data: DataSearchResponse = await response.json();
      searchResponse = data;
      return data;
    } catch (err) {
      console.error("Error fetching dataset:", err);
      throw err;
    }
  }
</script>

<div class="container-fluid">
  <div class="row">
    <div class="col-lg-2 gx-0">
      <SideSearch>
        <CheckboxSearchInput slot="datatypes"
                             searchUrlQueryParam="dataTypes"
                             values={dataTypes}
                             title="Radar VCP"
                             tooltip="#vcps-tooltip"
        />
        <CheckboxSearchInput slot="channels"
                             searchUrlQueryParam="channels"
                             values="{datasetMetadata.ncei.access.search.channels.params[0].values}"
                             title="Channel"
                             tooltip="<p>Select a channel to narrow the search by a specific channel.</p>"
        />
        <CheckboxSearchInput slot="accessLevel"
                             searchUrlQueryParam="icao"
                             url={new URL(datasetMetadata.ncei.access.search.icao.params[0].list)}
                             title="ICAO"
                             tooltip="<p>4-character International Civil Aviation Organization (ICAO) code for the NEXRAD site. Includes test sites.</p>"
        />

        <CheckboxSearchInput slot="keywords"
                             searchUrlQueryParam="event"
                             values="{datasetMetadata.ncei.access.search.event.params[0].values}"
                             title="{datasetMetadata.ncei.access.search.event.params[0].interfaceOptions.displayName}"
                             tooltip="<p>Event Description searched by keyword.</p>"
        />
        <LocationSearchInput slot="where" />
        <svelte:component
          this={whenComponentMap.find((e) => e.format === dateFormat).component}
          slot="when"
          format={dateFormat}
          enabled={!!dateFormat}
          minDate={datasetSummary?.startDate || '0001-01-01T00:00:00'}
          maxDate={datasetSummary?.endDate || '9999-12-31T23:59:59'}
        />
      </SideSearch>
    </div>

    <main class="col-md-10">
      {#await datasetLoaded}
        <div class="loading-container">
          <div
            class="spinner-border text-secondary"
            role="status"
            style="width: 3rem; height: 3rem;"
          >
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      {:then searchResponse}
        <!-- Title Section -->
        <div class="row dataset">
          <div class="col pt-3 ps-3">
            <h2 style="color: white;">{datasetMetadata?.name}</h2>
            <p style="color: white;" class="lead">
              Browse station files by map or file list, using any search options to narrow down your
              search.
            </p>
          </div>
        </div>

        <!-- Map Section -->
        <div class="row" style="background-color: #F1F3F6;">
          <div class="col-5 px-0">
            <MapComponent dataResults={searchResponse} homrPrefix={datasetMetadata?.ncei.access.search.station.homrprefix} mapSize={400} />
          </div>
          <div class="col-7 mb-2">
            <div class="pt-3">
              <div>
                <h2>Description</h2>
                <div style="height: 330px;" class="overflow-scroll">
                  <p>{datasetMetadata.ncei.description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <GenericCollectionDetails resultAggregations={searchResponse?.aggs} datasetAggregations={datasetSummary} />
        <div class="my-4"></div>

        <!-- Results summary and controls -->
        <div class="row">
          <div class="col">
            <div class="d-flex flex-column flex-sm-column flex-md-column flex-lg-row align-items-center justify-content-between mt-2 pb-2">
<!--              <div class="p-2">-->
                <!--              This is going to become something else.-->
                <!--                <SelectAllButton {searchResponse} {searchEndpoint} {datasetMetadata} />-->
<!--              </div>-->

                <div class="p-2 d-flex flex-row align-items-center">
                  <span class="px-2">Result Count:</span>
                  <span
                    class:text-danger={searchResponse?.aggs?.count >
                    datasetMetadata?.ncei.access.order?.maxfilecount}
                  >
                    {searchResponse?.aggs?.count}</span>
                  <span class="px-2"> of {datasetSummary?.count}</span>
                </div>
                {#if datasetMetadata?.ncei.access.order}
                <div class="p-2 d-flex flex-row align-items-center">
                  <div class:text-danger={searchResponse?.aggs?.count >
                    datasetMetadata?.ncei.access.order?.maxfilecount}>Order Limit: {datasetMetadata?.ncei.access.order?.maxfilecount}
                  </div>
                  <TooltipHybridButton
                    searchUrlQueryParam="resultCountAndLimit"
                    tooltip="#result-count-limit-tooltip"
                  />
                </div>
                {/if}

              {#if datasetMetadata.bulkdownload}
                <div class="p-2 d-flex flex-row align-items-center">
                  <div class="dropdown">
                    <button
                      class="btn ae-tertiary dropdown-toggle me-1"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <i class="bi bi-download"></i>
                      Bulk Download
                    </button>
                    <ul class="dropdown-menu">
                      {#each datasetMetadata.ncei.links.access.filter((a) => a.type === "download") as a}
                        <li><a class="dropdown-item" href={a.url}>{a.name}</a></li>
                      {/each}
                    </ul>
                  </div>
                  <TooltipHybridButton
                    searchUrlQueryParam="bulk-download"
                    tooltip="View all the files for this dataset and download them without having to place orders."
                  />
                  <!--                  <div>-->
                  <!--                    <i-->
                  <!--                      class="bi bi-question-circle-fill"-->
                  <!--                      id="bulkDownloadToolTip"-->
                  <!--                      aria-label="Bulk Download Tool Tip"-->
                  <!--                      aria-describedby="bulkDownloadToolTipContent"-->
                  <!--                    ></i>-->
                  <!--                    <Tooltip-->
                  <!--                      id="bulkDownloadToolTipContent"-->
                  <!--                      placement="top"-->
                  <!--                      target={`bulkDownloadToolTip`}-->
                  <!--                      role="tooltip"-->
                  <!--                    >-->
                  <!--                      <p>-->
                  <!--                        View all the files for this dataset and download them without having to-->
                  <!--                        place orders.-->
                  <!--                      </p>-->
                  <!--                    </Tooltip>-->
                  <!--                  </div>-->
                </div>
              {/if}
            </div>

            <!-- Dataset Cards -->
            <div class="row">
              <div class="col">
                <VCPDataSearchResults {searchResponse} {datasetMetadata} />

                <!-- Pagination -->
                {#if searchResponse?.aggs?.count}
                  <div class="row">
                    <div class="col">
                      <Pageination pageSize={PAGE_SIZE} count={searchResponse?.aggs?.count} />
                    </div>
                  </div>
                {/if}
              </div>
            </div>
          </div>
        </div>
      {:catch error}
        <div id="error-container">
          <div id="error-card">
            <p class="custom-error-message">
              Something went wrong
              <br />
              {error}
            </p>
          </div>
        </div>
      {/await}

      <!-- Success Notification (always in DOM) -->
      <CartNotification />

      <!-- Cart alert (conditionally shown) -->
      {#if $fileStore.selectedFiles.length > 0 && $showCartAlert}
        <CartAlert {datasetMetadata} />
      {/if}

      <!-- Modal -->
      <OrderOptionsModal selectedFileList={$fileStore.selectedFiles} {datasetMetadata} />
    </main>
  </div>
</div>

<style>
    #error-container {
        height: 45vh;
    }

    #error-card {
        display: flex;
        width: 45%;
        background-color: #e7b1b6;
        margin: 3vh auto;
        border-radius: 2vh;
        padding: 2vh;
    }

    #error-container #error-card .custom-error-message {
        font-size: clamp(1rem, 2vw, 1.5rem);
        margin: auto;
    }

    .loading-container {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 200px;
        width: 100%;
    }

    .scroll-shadow {
      max-height: 325px;
      overflow: auto;

      background: /* Shadow Cover TOP */ linear-gradient(
              #F1F3F6 80%,
              rgba(241, 243, 246, 0)
      ) center top,
        /* Shadow Cover BOTTOM */ linear-gradient(
              rgba(241, 243, 246, 0),
              #F1F3F6 70%
      ) center bottom,
        /* Shadow TOP */ radial-gradient(
              farthest-side at 50% 0,
              rgba(0, 0, 0, 0.2),
              rgba(0, 0, 0, 0)
      ) center top,
        /* Shadow BOTTOM */ radial-gradient(
              farthest-side at 50% 100%,
              rgba(0, 0, 0, 0.2),
              rgba(0, 0, 0, 0)
      ) center bottom;

      background-repeat: no-repeat;
      background-size: 100% 40px, 100% 40px, 100% 14px, 100% 14px;
      background-attachment: local, local, scroll, scroll;
    }
</style>
